/**
 * ResearchJobsNotifier — o relógio do aviso de busca em segundo plano.
 *
 * Montado DENTRO do roteador e FORA das rotas (`app/Router.tsx`), de propósito:
 * é isso que faz o aviso continuar andando quando o usuário troca de conversa
 * ou vai para outra rota. Montar dentro da página do chat devolveria o defeito
 * que a sprint conserta.
 *
 * NÃO É ROTA E NÃO DESENHA TELA: não renderiza nada (`null`). O que aparece é o
 * aviso do `react-hot-toast`, o mesmo sistema dos outros avisos do produto.
 */
import { useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useResearchJobsStore } from '../../stores/researchJobsStore'
import { useConnectionStore } from '../../stores/connectionStore'
import { syncResearchNotices } from '../../lib/researchNotice'
import { useManualIngestPolling } from './useManualIngestPolling'

/** O contador anda de segundo em segundo; o motor só fala a cada 15s. */
const TICK_MS = 1000

export default function ResearchJobsNotifier() {
  const navigate = useNavigate()
  const jobs = useResearchJobsStore((s) => s.jobs)
  // Andamento do manual grande (o motor não o publica por socket).
  useManualIngestPolling()

  // Lê o estado DIRETO da store, nunca de uma cópia capturada: o aviso muda de
  // texto no mesmo instante em que o frame do motor chega, sem esperar o React
  // repintar. Ler de um `ref` atualizado no render deixava o relógio um passo
  // atrás e o aviso de FIM aparecia como se a busca ainda estivesse correndo.
  const tick = useCallback(() => {
    syncResearchNotices(
      useResearchJobsStore.getState().jobs,
      Date.now(),
      (conversationId) => navigate(`/chat/${conversationId}`),
      (jobId) => useResearchJobsStore.getState().clear(jobId),
    )
  }, [navigate])

  // Um trabalho que nasce ou muda de estado aparece na hora...
  useEffect(() => {
    tick()
  }, [jobs, tick])

  // T10 — o que reergue o trabalho quando o AVISO se perdeu: aba fechada,
  // recarga da página, socket caído e reconectado. Sem esta lista o usuário
  // volta e não há vestígio nenhum de que a busca existe. Roda no carregamento
  // e a CADA reconexão, porque é justamente durante a queda que os avisos somem.
  const connectionState = useConnectionStore((s) => s.state)
  useEffect(() => {
    if (connectionState !== 'CONNECTED') return
    void useResearchJobsStore.getState().hydrate()
  }, [connectionState])

  // ...e o contador anda sozinho enquanto ninguém mexe em nada.
  useEffect(() => {
    const timer = setInterval(tick, TICK_MS)
    return () => clearInterval(timer)
  }, [tick])

  return null
}
